import { Page, SectionBlock } from "../components/Page";

export function Foundation() {
  return (
    <Page
      number="01"
      title="Foundation"
      eyebrow="Who we are"
      intro={
        <>
          Vihangam Yog Australia is the Sydney chapter of a lineage of
          meditation taught for centuries — quietly, plainly, and freely. This
          page is the lens for every decision the brand makes: when in doubt,
          return here.
        </>
      }
    >
      <SectionBlock title="What we exist for">
        <p className="font-display text-[28px] leading-[1.18] italic text-ink">
          To make a real meditation practice — twenty minutes a day, ten in the
          morning and ten in the evening —{" "}
          <span className="text-amber">approachable</span>,{" "}
          <span className="text-amber">unpretentious</span>, and{" "}
          <span className="text-amber">always free</span> for anyone in Sydney
          who'd like to learn.
        </p>
        <p className="mt-6 max-w-[620px] font-body text-[16px] leading-relaxed text-ink-soft">
          We're not a wellness brand. We're not a startup. We're a volunteer-run
          chapter of a long-standing organisation, teaching one technique with
          care. Everything we make should feel like that.
        </p>
      </SectionBlock>

      <SectionBlock title="Audience — who this is for">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              tag: "First-time sitters",
              body: "Curious, possibly skeptical, want a low-friction way to try meditation without buying into anything. Likely to come via a Sunday Sydney Meetup.",
            },
            {
              tag: "Returning practitioners",
              body: "Already sit. Want to deepen, build a streak, or reconnect with the practice after a gap. Likely to engage with the 11-Day Challenge.",
            },
            {
              tag: "Friends of the practice",
              body: "Family of practitioners, peers, broader community. Want context for what we do without theological weight.",
            },
          ].map((a) => (
            <div
              key={a.tag}
              className="surface p-5"
              style={{ background: "var(--paper)" }}
            >
              <p className="font-flair text-[22px] text-amber">{a.tag}</p>
              <p className="mt-3 font-body text-[14px] leading-relaxed text-ink-soft">
                {a.body}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="What we are / what we are not">
        <div className="grid gap-6 md:grid-cols-2">
          <div
            className="border-l-2 pl-5"
            style={{ borderColor: "var(--amber)" }}
          >
            <p className="eyebrow text-amber">We are</p>
            <ul className="mt-4 space-y-3 font-body text-[15px] text-ink">
              {[
                "Volunteer-run, donation-funded, always free.",
                "Lineage-rooted. The technique we teach is centuries old.",
                "Sydney-specific. We hold sessions here. We know this city.",
                "Plain-spoken. We don't dress up the practice.",
                "Welcoming to anyone, regardless of background or belief.",
              ].map((line) => (
                <li key={line} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-amber" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
          <div
            className="border-l-2 pl-5"
            style={{ borderColor: "var(--ink-soft)" }}
          >
            <p className="eyebrow text-ink-soft">We are not</p>
            <ul className="mt-4 space-y-3 font-body text-[15px] text-ink-soft">
              {[
                "A wellness brand or a productivity hack.",
                "App-led. We exist mostly through people meeting in person.",
                "A subscription, a course, or a course-funnel.",
                "Trying to be hip. We are not chasing trends.",
                "A theology. We teach a technique; the rest is up to you.",
              ].map((line) => (
                <li key={line} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-ink-soft" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Principles">
        <div className="grid gap-3 md:grid-cols-2">
          {[
            [
              "Less, but considered",
              "We'd rather make one thing well than three things quickly. Whitespace is welcome.",
            ],
            [
              "Plain over poetic",
              "Cleverness ages badly. Plain language ages well.",
            ],
            [
              "Photography over illustration",
              "Real moments carry weight that drawn figures don't.",
            ],
            [
              "Warmth without theatre",
              "The practice is warm. The communication shouldn't feel performed.",
            ],
            ["Editorial, not retail", "We are not selling. We are inviting."],
            [
              "Free, said quietly",
              "It's free. That should be a footnote, not a banner.",
            ],
          ].map(([title, body]) => (
            <div
              key={title}
              className="border-t pt-5 border-rule first:border-t-0 md:border-t-0 md:border-l md:pt-0 md:pl-6"
            >
              <p className="font-display text-[22px] text-ink">{title}</p>
              <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-soft">
                {body}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>
    </Page>
  );
}
